import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SecureStorageService {
  // Note: this key is bundled in frontend code and offers obfuscation, not strong secrecy.
  private readonly storageKey = `${environment.SSO.client_id}:${environment.envName}:fk-recon-storage-v1`;

  private encrypt(value: string): string {
    return CryptoJS.AES.encrypt(value, this.storageKey).toString();
  }

  private decrypt(encrypted: string): string | null {
    try {
      const bytes = CryptoJS.AES.decrypt(encrypted, this.storageKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return decrypted || null;
    } catch {
      return null;
    }
  }

  setSessionString(key: string, value: string): void {
    const encrypted = this.encrypt(value);
    sessionStorage.setItem(key, encrypted);
  }

  getSessionString(key: string): string | null {
    const encrypted = sessionStorage.getItem(key);
    if (!encrypted) {
      return null;
    }

    return this.decrypt(encrypted);
  }

  setSessionObject<T>(key: string, value: T): void {
    this.setSessionString(key, JSON.stringify(value));
  }

  getSessionObject<T>(key: string): T | null {
    const raw = this.getSessionString(key);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  removeSession(key: string): void {
    sessionStorage.removeItem(key);
  }

  clearSession(): void {
    sessionStorage.clear();
  }

  setLocalString(key: string, value: string): void {
    const encrypted = this.encrypt(value);
    localStorage.setItem(key, encrypted);
  }

  getLocalString(key: string): string | null {
    const encrypted = localStorage.getItem(key);
    if (!encrypted) {
      return null;
    }

    return this.decrypt(encrypted);
  }

  setLocalObject<T>(key: string, value: T): void {
    this.setLocalString(key, JSON.stringify(value));
  }

  getLocalObject<T>(key: string): T | null {
    const raw = this.getLocalString(key);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  removeLocal(key: string): void {
    localStorage.removeItem(key);
  }

  clearLocal(): void {
    localStorage.clear();
  }
}